import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Chat from '../Chat/chat';
import LoginV3 from '../Chat/Login/login-v3';
import { apiCall } from '../utils/api';
import Loading from '../components/Loading/Loading';

const EXPERT_SLUG_MAP = {
    nishantkumar: ['nishant kumar', 'nishantkumar', 'nishant'],
    scannerexpert: ['scanner expert', 'scannerexpert', 'scanneradda expert', 'scanneradda'],
    neerajarora: ['neeraj arora', 'neerajarora', 'neeraj'],
    aakashkandoi: ['aakash kandoi', 'aakashkandoi', 'aakash'],
    vanshgoyal: ['vansh goyal', 'vanshgoyal', 'vansh'],
    kushallodha: ['kushal lodha', 'kushallodha', 'kushal'],
    umeshsharma: ['umesh sharma', 'umeshsharma', 'umesh'],
    'jksc-audit-expert': ['jksc audit expert', 'jksc', 'jk sc audit', 'jksc audit', 'audit expert'],
    paraggupta: ['parag gupta', 'paraggupta', 'parag'],
    gujaratgovt: ['gujarat government ai expert', 'gujaratgovt', 'gujarat government', 'gujarat ai expert', '69c0cfdef4f772ce6f8ce5b2'],
    karan: ['karan', 'karan lendingkart', 'lendingkart'],
    astrodeepak: ['astro deepak', 'astrodeepak', 'deepak'],
};

const INTRO_MESSAGES = {
    nishantkumar: 'Hi! I’m CA Nishant Kumar—ask me anything about taxation or CA careers.',
    scannerexpert: 'Scanner Expert here. Share your case, and I’ll scan it fast.',
    neerajarora: 'Hey! I’m Neeraj Arora; let’s talk growth plans and job readiness.',
    aakashkandoi: 'Hi, I am CA Aakash Kandoi. Need help with accounting or incomplete audit reports?',
    vanshgoyal: 'Hello, Vansh Goyal at your service—let’s unlock your income goals.',
    kushallodha: 'Kushal Lodha here. Ask me about finance, investing, or career moves.',
    umeshsharma: "Hi! I'm CA Umesh Sharma—let me help you with your CA queries.",
    'jksc-audit-expert': "Hi! I'm the JKSC Audit Expert - share your toughest audit questions.",
    paraggupta: "Hi, I'm CA Parag Gupta—ask me about audit strategy, client readiness, or career moves.",
    gujaratgovt: "Hello! Gujarat Government AI Expert at your service for state-specific government career guidance.",
    karan: "Hi! I'm Karan—how can I help you today?",
    astrodeepak: "Hi! I'm Astro Deepak—ask me anything about astrology.",
};

const GUJARAT_GOVT_CHARACTER_ID = '69c0cfdef4f772ce6f8ce5b2';

const findCharacterById = (list, id) => {
    if (!Array.isArray(list) || !id) return null;
    const normalizedId = String(id);
    return list.find((item) => {
        const candidateId = resolveCharacterId(item);
        return candidateId && String(candidateId) === normalizedId;
    }) || null;
};

const resolveCharacterId = (item) => {
    if (!item) return null;
    if (typeof item === 'string') return item;
    if (Array.isArray(item)) return resolveCharacterId(item[0]);
    if (item.character) {
        const nestedId = resolveCharacterId(item.character);
        if (nestedId) return nestedId;
    }
    return (
        item._id ||
        item.id ||
        item.character_id ||
        item.characterId ||
        item.characterID ||
        null
    );
};

const normalizeForMatch = (value) => {
    if (!value) return '';
    return value.toLowerCase().replace(/[^a-z0-9]/g, '');
};

const findCharacterMatch = (list = [], names = []) => {
    if (!Array.isArray(list) || list.length === 0 || names.length === 0) return null;

    return list.find((item) => {
        const candidateNames = [
            item?.name,
            item?.character?.name,
            item?.display_name,
            item?.alias,
        ].filter(Boolean);

        const normalizedTargets = names.map(normalizeForMatch);

        return candidateNames.some((candidate) => {
            const normalizedCandidate = normalizeForMatch(candidate);
            return normalizedTargets.some((target) =>
                normalizedCandidate.includes(target) || target.includes(normalizedCandidate)
            );
        });
    });
};

const DirectChat = ({ slug, darkMode, toggleDarkMode, themeClass = '' }) => {
    const [character, setCharacter] = useState(null);
    const [loading, setLoading] = useState(true);
    const [hasSubscription, setHasSubscription] = useState(false);
    const [needsLogin, setNeedsLogin] = useState(false);
    const navigate = useNavigate();

    const targetNames = EXPERT_SLUG_MAP[slug?.toLowerCase()] || [];

    // Store redirect path immediately on mount (before any async calls)
    useEffect(() => {
        const currentPath = window.location.pathname + window.location.search;
        if (currentPath && currentPath !== '/') {
            localStorage.setItem('postLoginRedirect', currentPath);
        }
    }, []);

    useEffect(() => {
        const init = async () => {
            try {
                const loginCheck = await apiCall.get('/users/check/login');
                const loggedIn = loginCheck?.data?.is_logged_in;

                if (!loggedIn) {
                    setNeedsLogin(true);
                    setLoading(false);
                    return;
                }
                
                // User is logged in, clear the stored redirect since we're handling it now
                localStorage.removeItem('postLoginRedirect');

                const [companionsRes, subscriptionRes] = await Promise.all([
                    apiCall.get('/chat/companions'),
                    apiCall.get('/subscriptions/check').catch(() => null),
                ]);

                if (subscriptionRes) {
                    const subPayload = subscriptionRes?.data ?? subscriptionRes;
                    setHasSubscription(Boolean(subPayload?.has_subscription));
                }

                const payload = companionsRes?.data ?? companionsRes;
                const data = payload?.data ?? payload;

                const extractArray = (obj, keys) => {
                    if (!obj) return [];
                    for (const key of keys) {
                        if (Array.isArray(obj[key])) return obj[key];
                    }
                    if (Array.isArray(obj)) return obj;
                    return [];
                };

                const allowedCharacters = extractArray(data, [
                    'allowed_characters',
                    'allowedCharacters',
                    'characters',
                    'companions',
                    'character_list',
                ]);

                let match = null;
                if (slug?.toLowerCase() === 'gujaratgovt') {
                    match = findCharacterById(allowedCharacters, GUJARAT_GOVT_CHARACTER_ID);
                }
                if (!match) {
                    match = findCharacterMatch(allowedCharacters, targetNames);
                }

                if (!match) {
                    const charsResponse = await apiCall.get('/character/profile/list');
                    const listPayload = charsResponse?.data ?? charsResponse;
                    const characters = Array.isArray(listPayload) ? listPayload : listPayload?.data ?? [];
                    if (slug?.toLowerCase() === 'gujaratgovt') {
                        match = findCharacterById(characters, GUJARAT_GOVT_CHARACTER_ID);
                    }
                    if (!match) {
                        match = findCharacterMatch(characters, targetNames);
                    }
                }

                if (match) {
                    const normalized = match.character && typeof match.character === 'object'
                        ? { ...match.character, ...match }
                        : match;
                    const resolvedId = resolveCharacterId(normalized) || slug?.toLowerCase();
                    normalized._id = normalized._id || resolvedId;
                    normalized.id = normalized.id || resolvedId;
                    if (/scanneradda/i.test(normalized.name)) {
                        normalized.name = normalized.name.replace(/scanneradda/gi, 'Scanner');
                    }
                    normalized.intro_message = normalized.intro_message || INTRO_MESSAGES[slug?.toLowerCase()];
                    normalized.displayMessage = normalized.displayMessage || normalized.intro_message || 'Tap to start chatting...';
                    setCharacter(normalized);
                } else {
                    navigate('/', { replace: true });
                }
            } catch (error) {
                console.error('DirectChat init error:', error);
                setNeedsLogin(true);
            } finally {
                setLoading(false);
            }
        };

        if (targetNames.length > 0) {
            init();
        } else {
            navigate('/', { replace: true });
        }
    }, [slug, targetNames, navigate]);

    const handleBack = () => {
        navigate('/', { replace: true });
    };

    if (loading) {
        const bgColor = themeClass.includes('theme-orange') ? '#F57C20' : '#060712';
        return (
            <div style={{ height: '100vh', backgroundColor: bgColor }}>
                <Loading isLoading={true} />
            </div>
        );
    }

    if (needsLogin) {
        const isOrange = themeClass.includes('theme-orange');
        const isAstro = slug?.toLowerCase() === 'astrodeepak';
        const loginBg = isAstro
            ? 'linear-gradient(135deg, #f0f7ef 0%, #e8f5e6 50%, #f0f7ef 100%)'
            : isOrange
                ? 'linear-gradient(135deg, #FFF8F0 0%, #FDEBD0 50%, #FFF8F0 100%)'
                : '#060712';
        const loginTitle = isAstro ? 'AI Astro' : 'Lending Kart';
        const loginTitleColor = isAstro ? '#61A456' : isOrange ? '#F57C20' : '#fff';
        const loginSubColor = isAstro ? '#7ab86e' : isOrange ? '#C68A50' : '#aaa';
        return (
            <div className={isOrange ? 'login-orange-page' : ''} style={{
                minHeight: '100dvh',
                background: loginBg,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
            }}>
                <div style={{ textAlign: 'center', marginBottom: 32 }}>
                    <h1 style={{
                        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                        fontSize: 36,
                        fontWeight: 800,
                        color: loginTitleColor,
                        letterSpacing: '-0.02em',
                        margin: 0,
                    }}>
                        {loginTitle}
                    </h1>
                    <p style={{
                        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                        fontSize: 14,
                        color: loginSubColor,
                        marginTop: 6,
                    }}>
                        Sign in to continue
                    </p>
                </div>
                <div style={{ width: '100%', maxWidth: 400 }}>
                    <LoginV3 darkMode={darkMode} toggleDarkMode={toggleDarkMode} hideFirmLogin />
                </div>
            </div>
        );
    }

    if (!character) {
        return null;
    }

    const isAstroDeepak = slug?.toLowerCase() === 'astrodeepak';

    return (
        <Chat
            isUnidentifiedUser={false}
            darkMode={darkMode}
            toggleDarkMode={toggleDarkMode}
            selectedCharacter={character}
            onBackToCharacterSelection={handleBack}
            hasSubscription={hasSubscription}
            themeClass={themeClass}
            hideHeaderExtras={isAstroDeepak}
            instantSend={isAstroDeepak}
        />
    );
};

export default DirectChat;
